import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { z } from 'zod';

import styles from './KeyStats.module.scss';

export const KEY_STATS_MAX_ITEMS = 12;
export const KEY_STATS_MAX_VALUE_SEGMENTS = 4;
export const KEY_STATS_MAX_ITEMS_PER_ROW = 6;

export const keyStatsValueSegmentSchema = z
  .object({
    text: z.string().min(1),
    colorVariant: z.enum(['bullish', 'bearish']).optional(),
  })
  .strict();

export const keyStatsItemSchema = z
  .object({
    label: z.string().min(1),
    value: z
      .array(keyStatsValueSegmentSchema)
      .min(1)
      .max(KEY_STATS_MAX_VALUE_SEGMENTS),
  })
  .strict();

export const keyStatsPropsSchema = z
  .object({
    items: z.array(keyStatsItemSchema).min(1).max(KEY_STATS_MAX_ITEMS),
    itemsPerRow: z
      .number()
      .int()
      .positive()
      .max(KEY_STATS_MAX_ITEMS_PER_ROW)
      .optional(),
  })
  .strict();

/** One inline fragment of a stat value (e.g. primary figure vs. delta). */
export type ValueSegment = z.infer<typeof keyStatsValueSegmentSchema>;

/** A single labeled stat tile in the grid. */
export type KeyStatItem = z.infer<typeof keyStatsItemSchema>;

export type KeyStatsProps = z.infer<typeof keyStatsPropsSchema>;

type KeyStatsStyle = CSSProperties & {
  '--key-stats-items-per-row': number;
};

/**
 * Responsive grid of labeled metric tiles for financial or KPI summaries.
 *
 * Each tile shows a muted label and a value built from inline `ValueSegment`s.
 * Use multiple segments to pair a primary figure with a colored delta or qualifier
 * (e.g. `"$12.4B"` + `"+3.2%"` with `colorVariant: 'bullish'`).
 *
 * Layout is CSS-grid based: `itemsPerRow` sets the column cap; tiles wrap
 * automatically when there are more items than columns.
 */
export const KeyStats = ({ items, itemsPerRow = 4 }: KeyStatsProps) => {
  const rootStyle: KeyStatsStyle = {
    '--key-stats-items-per-row': itemsPerRow,
  };

  return (
    <div
      className={styles.root}
      style={rootStyle}
      data-clipboard-snapshot
      data-key-stats
    >
      {items.map((item, i) => (
        <div key={i} className={styles.tileCard}>
          <span className={styles.tileLabel}>{item.label}</span>
          <span className={styles.tileValue}>
            {item.value.map((segment, i) => (
              <span
                key={i}
                className={clsx({
                  [styles.bullish]: segment.colorVariant === 'bullish',
                  [styles.bearish]: segment.colorVariant === 'bearish',
                })}
              >
                {segment.text}
              </span>
            ))}
          </span>
        </div>
      ))}
    </div>
  );
};
